import React from 'react';
import { GenericTag } from '@ui-components/Tag/GenericTag';
import GenericDropdown from '@ui-components/Dropdown/GenericDropdown';
import { OdooOrderLine, ProductAssignment, ProductCategory, formatOrderAmount } from './types';
import {
  childIdsOf,
  cpePortCandidates,
  decodeCpePortId,
  nestedCandidates,
  parentIdsOf,
  MAX_PORTS_PER_CPE,
  NESTED_CHILD_CATEGORIES
} from './hierarchy';

type TagVariant = 'success' | 'warning' | 'error' | 'default' | 'neutral';

// The chain is at most Last Mile -> CPE -> Port -> Internet -> IP Block; the
// guard only protects the render from a malformed (cyclic) saved design.
const MAX_DEPTH = 6;

export interface ServiceTreeContext {
  linesById: Map<number, OdooOrderLine>;
  assignableLines: OdooOrderLine[];
  assignments: ProductAssignment[];
  reachedLineIds: Set<number>;
  currency?: string | null;
  tagVariant: (category: ProductCategory) => TagVariant;
  // Page-owned inputs for a line (Last Mile address, Cloud config)
  renderDetails: (line: OdooOrderLine) => React.ReactNode;
  onAssign: (child: OdooOrderLine, parent: OdooOrderLine) => void;
  onRemove: (childId: number, parentId: number) => void;
  // Ports of a CPE (synthetic ids, see cpePortId)
  onAssignToCpePort: (child: OdooOrderLine, portId: number) => void;
  onAddCpePort: (cpeLineId: number) => void;
  onRemoveCpePort: (portId: number) => void;
}

interface AssignedServiceNodeProps {
  line: OdooOrderLine;
  parentId: number;
  ctx: ServiceTreeContext;
  depth?: number;
}

// Dropdown items are plain strings, so the label must tell apart two lines
// with the same SKU (e.g. two /29 IP Blocks)
export const serviceOptionLabel = (line: OdooOrderLine): string =>
  `${line.product.categoryLabel}: ${line.product.sku || line.product.name} (${line.serviceId || `line ${line.id}`})`;

/**
 * The other parents of a line that hangs from several (a CPE reached through
 * two Last Miles), described by the Port each of them lands on.
 */
function otherUplinks(line: OdooOrderLine, parentId: number, ctx: ServiceTreeContext): string[] {
  return parentIdsOf(ctx.assignments, line.id)
    .filter(id => id !== parentId)
    .map(id => {
      const parent = ctx.linesById.get(id);
      const port = parentIdsOf(ctx.assignments, id)
        .map(portId => ctx.linesById.get(portId))
        .find(Boolean);
      const via = parent?.product.sku || `line ${id}`;
      return port ? `${via} → ${port.origin?.name || port.serviceId || port.id}` : via;
    });
}

interface CpePortNodeProps {
  portId: number;
  canRemove: boolean;
  ctx: ServiceTreeContext;
  depth: number;
}

/**
 * A Port of the CPE, created by presales. It becomes a draft KontrolEdgePort on
 * submit and delivers the services hung from it.
 */
function CpePortNode({ portId, canRemove, ctx, depth }: CpePortNodeProps) {
  const portNumber = decodeCpePortId(portId)?.portNumber;
  const services = childIdsOf(ctx.assignments, portId)
    .map(id => ctx.linesById.get(id))
    .filter((line): line is OdooOrderLine => Boolean(line));
  const candidates = cpePortCandidates(portId, ctx.assignableLines, ctx.assignments, ctx.reachedLineIds);

  return (
    <div className="rounded-lg border border-dashed border-semantic-border-secondary">
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2">
          <GenericTag label="CPE Port" variant="warning" size="small" />
          <span className="text-xs font-medium text-semantic-text-primary">Port {portNumber}</span>
          <span className="text-xs text-semantic-text-secondary">draft Port, created on submit</span>
        </div>
        {canRemove && (
          <button
            onClick={() => ctx.onRemoveCpePort(portId)}
            className="ml-2 text-semantic-error hover:text-red-700 text-xs"
            title="Remove Port"
          >
            ✕
          </button>
        )}
      </div>
      <div className="px-3 pb-3 space-y-2">
        {services.map(service => (
          <AssignedServiceNode key={service.id} line={service} parentId={portId} ctx={ctx} depth={depth + 1} />
        ))}
        {candidates.length > 0 && (
          <GenericDropdown
            items={candidates.map(serviceOptionLabel)}
            defaultLabel={`+ Assign to Port ${portNumber}...`}
            maxWidthClass="w-full"
            dropdownMaxHeightClass="max-h-48"
            fixedPosition={true}
            onSelect={(selected) => {
              const child = candidates.find(c => serviceOptionLabel(c) === selected);
              if (child) ctx.onAssignToCpePort(child, portId);
            }}
          />
        )}
      </div>
    </div>
  );
}

/**
 * The Ports of a CPE and the button to add one. A CPE always keeps one Port.
 */
function CpePorts({ cpe, ctx, depth }: { cpe: OdooOrderLine; ctx: ServiceTreeContext; depth: number }) {
  const portIds = childIdsOf(ctx.assignments, cpe.id).filter(id => decodeCpePortId(id)?.cpeLineId === cpe.id);

  return (
    <div className="px-3 pb-3 pt-2 border-t border-semantic-border-secondary space-y-2 border-l-2 ml-3">
      {portIds.map(portId => (
        <CpePortNode key={portId} portId={portId} canRemove={portIds.length > 1} ctx={ctx} depth={depth} />
      ))}
      {portIds.length < MAX_PORTS_PER_CPE && (
        <button
          type="button"
          onClick={() => ctx.onAddCpePort(cpe.id)}
          className="text-xs font-medium text-brand-primary hover:underline"
        >
          + Add Port
        </button>
      )}
    </div>
  );
}

export default function AssignedServiceNode({ line, parentId, ctx, depth = 0 }: AssignedServiceNodeProps) {
  const children = childIdsOf(ctx.assignments, line.id)
    .map(id => ctx.linesById.get(id))
    .filter((child): child is OdooOrderLine => Boolean(child));
  const acceptsChildren = Boolean(NESTED_CHILD_CATEGORIES[line.product.category]) && depth < MAX_DEPTH;
  const candidates = acceptsChildren
    ? nestedCandidates(line, ctx.assignableLines, ctx.assignments, ctx.reachedLineIds)
    : [];
  const sharedVia = otherUplinks(line, parentId, ctx);

  return (
    <div className="bg-semantic-bg-primary rounded-lg border border-semantic-border-secondary overflow-hidden">
      {/* Header row */}
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
          <GenericTag
            label={line.product.categoryLabel}
            variant={ctx.tagVariant(line.product.category)}
            size="small"
          />
          <span className="text-xs text-semantic-text-primary truncate">
            {line.product.sku || line.product.name}
          </span>
          {line.serviceId && (
            <span className="text-xs font-mono text-semantic-text-secondary">{line.serviceId}</span>
          )}
          {line.destination && (
            <span className="text-xs text-semantic-text-secondary">→ {line.destination.name}</span>
          )}
        </div>
        <span className="ml-2 text-xs font-medium text-semantic-text-primary whitespace-nowrap">
          {formatOrderAmount(line.subtotal, ctx.currency)}
        </span>
        <button
          onClick={() => ctx.onRemove(line.id, parentId)}
          className="ml-2 text-semantic-error hover:text-red-700 text-xs"
          title="Remove assignment"
        >
          ✕
        </button>
      </div>

      {sharedVia.length > 0 && (
        <p className="px-3 pb-2 text-xs text-semantic-text-secondary">
          Shared · also via {sharedVia.join(', ')}
        </p>
      )}

      {ctx.renderDetails(line)}

      {line.product.category === 'cpe' && depth < MAX_DEPTH && <CpePorts cpe={line} ctx={ctx} depth={depth} />}

      {/* Nested services (Last Mile -> CPE, Internet -> IP Block) */}
      {(children.length > 0 || candidates.length > 0) && (
        <div className="px-3 pb-3 pt-2 border-t border-semantic-border-secondary space-y-2 border-l-2 ml-3">
          {children.map(child => (
            <AssignedServiceNode key={child.id} line={child} parentId={line.id} ctx={ctx} depth={depth + 1} />
          ))}
          {candidates.length > 0 && (
            <GenericDropdown
              items={candidates.map(serviceOptionLabel)}
              defaultLabel={`+ Assign to ${line.product.categoryLabel}...`}
              maxWidthClass="w-full"
              dropdownMaxHeightClass="max-h-48"
              fixedPosition={true}
              onSelect={(selected) => {
                const child = candidates.find(c => serviceOptionLabel(c) === selected);
                if (child) ctx.onAssign(child, line);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
